import { useEffect, useState, createContext, useContext, useCallback, ReactNode } from 'react';
import { Calendar, Sparkles, X } from 'lucide-react';
import BookDemoModal from '@/components/BookDemoModal';
import { useLanguage } from '@/contexts/LanguageContext';

/* ------------------------------------------------------------------ */
/* Global modal context — one modal, opened from anywhere on the site */
/* ------------------------------------------------------------------ */

type Ctx = { open: () => void };
const DemoModalCtx = createContext<Ctx>({ open: () => {} });
export const useDemoModal = () => useContext(DemoModalCtx);

export function DemoModalProvider({ children }: { children: ReactNode }) {
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [exitShown, setExitShown] = useState(false);
  const lang = (language === 'fr' ? 'fr' : 'en') as 'en' | 'fr';

  const handleOpen = useCallback(() => setOpen(true), []);

  // Exit-intent: mouse leaving from the top of the viewport
  useEffect(() => {
    if (exitShown) return;
    const onLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && !open) {
        setOpen(true);
        setExitShown(true);
      }
    };
    document.addEventListener('mouseout', onLeave);
    return () => document.removeEventListener('mouseout', onLeave);
  }, [open, exitShown]);

  return (
    <DemoModalCtx.Provider value={{ open: handleOpen }}>
      {children}
      <BookDemoModal open={open} onClose={() => setOpen(false)} lang={lang} />
    </DemoModalCtx.Provider>
  );
}

/* ------------------------------------------------------------------ */
/* Inline button — drop anywhere                                       */
/* ------------------------------------------------------------------ */

interface DemoButtonProps {
  className?: string;
  variant?: 'primary' | 'outline' | 'ghost' | 'gold';
  size?: 'sm' | 'md' | 'lg';
  label?: string;
  icon?: boolean;
}

export function DemoButton({
  className = '',
  variant = 'primary',
  size = 'md',
  label,
  icon = true,
}: DemoButtonProps) {
  const { open } = useDemoModal();
  const { language } = useLanguage();
  const text =
    label ??
    (language === 'fr' ? 'Réserver une démo' : language === 'es' ? 'Solicitar demo' : 'Request a Demo');

  const base =
    'inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 whitespace-nowrap';
  const sizes = {
    sm: 'text-xs px-3 py-2 min-h-[40px]',
    md: 'text-sm px-4 py-2.5 min-h-[44px]',
    lg: 'text-base px-6 py-3 min-h-[48px]',
  }[size];
  const variants = {
    primary:
      'bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:scale-[1.02] border border-primary/40',
    outline:
      'border-2 border-primary/40 text-primary bg-background/60 backdrop-blur hover:bg-primary/10 hover:border-primary',
    ghost: 'text-primary hover:bg-primary/10',
    gold: 'bg-sovereign-navy text-white border-2 border-sovereign-gold hover:bg-sovereign-gold hover:text-sovereign-navy',
  }[variant];

  return (
    <button type="button" onClick={open} className={`${base} ${sizes} ${variants} ${className}`} aria-label={text}>
      {icon && <Calendar className="h-4 w-4" aria-hidden="true" />}
      {text}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Floating bottom-right CTA — visible on every page after scroll      */
/* ------------------------------------------------------------------ */

export function FloatingDemoCTA() {
  const { open } = useDemoModal();
  const { language } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem('qb_floating_demo_dismissed');
    if (stored === '1') setDismissed(true);
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (dismissed || !visible) return null;

  const label =
    language === 'fr' ? 'Démo gratuite' : language === 'es' ? 'Demo gratis' : 'Free Demo';
  const sub =
    language === 'fr' ? '15 min · sans engagement' : language === 'es' ? '15 min · sin compromiso' : '15 min · no commitment';

  return (
    <div className="fixed bottom-5 right-5 z-[90] flex items-end gap-2 print:hidden max-w-[calc(100vw-2rem)]">
      <button
        onClick={() => {
          setDismissed(true);
          sessionStorage.setItem('qb_floating_demo_dismissed', '1');
        }}
        className="mb-1 p-1.5 rounded-full bg-background/80 backdrop-blur border border-border text-muted-foreground hover:text-foreground shadow-sm"
        aria-label="Dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      <button
        onClick={open}
        className="group flex items-center gap-3 pl-4 pr-5 py-3 rounded-full bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-2xl shadow-primary/40 hover:shadow-primary/60 hover:scale-105 transition-all border border-primary/40 min-h-[52px]"
        aria-label={`${label} — ${sub}`}
      >
        <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-white/20">
          <Sparkles className="h-4 w-4" />
          <span className="absolute inset-0 rounded-full bg-white/30 animate-ping" />
        </span>
        <span className="flex flex-col items-start leading-tight">
          <span className="font-bold text-sm">{label}</span>
          <span className="text-[10px] opacity-90">{sub}</span>
        </span>
      </button>
    </div>
  );
}
